import { useState } from 'react';
import { Card } from './Card';
import { DT, DS2 } from '../../theme/designTokens';
import { gradientTextStyle } from './GradientText';

interface Props {
  number: string;
  title:  string;
  body:   string;
}

/**
 * DS2 numbered feature card (e.g. Landing "Why play here" 01/02/03).
 * Reuses the existing <Card> primitive for the box/border — only the
 * DS2 radius and typography are layered on top, so any future improvement
 * to <Card>'s hover/click behavior is inherited automatically.
 *
 * Corner hover-glow is Figma-exact (`radial-gradient(182.56% 237.03% at 100% 0%, ...)`)
 * — distinct from Card's own `hoverGlow` prop formula, so it's implemented locally
 * here via onMouseEnter/onMouseLeave (Card spreads ...rest last, so these override
 * Card's internal no-op handlers) rather than reusing that prop.
 */
export function NumberedFeatureCard({ number, title, body }: Props) {
  const [hovered, setHovered] = useState(false);
  return (
    <Card
      variant="bordered"
      padding={DS2.spacing.cardPadding}
      style={{
        position:     'relative',
        overflow:     'hidden',
        borderRadius: DS2.radius.card,
        border:       `1.44px solid ${DS2.color.cardBorder}`,
        height:       '100%',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        aria-hidden="true"
        style={{
          position:   'absolute',
          inset:      0,
          background: 'radial-gradient(182.56% 237.03% at 100% 0%, rgba(201,168,76,0.12) 0%, rgba(201,168,76,0) 60%)',
          opacity:    hovered ? 1 : 0,
          transition: 'opacity .15s cubic-bezier(0.4,0,0.2,1)',
          pointerEvents: 'none',
        }}
      />
      {/* Figma-confirmed 108.48deg gradient — distinct angle from the shared 104.72deg button gradient */}
      <div style={{ fontSize: `clamp(2rem, ${DS2.type.cardNumber.pref}vw, ${DS2.type.cardNumber.max}px)`, fontFamily: DS2.font.heading, fontWeight: 700, ...gradientTextStyle, background: 'linear-gradient(108.48deg, #F0D78C 0%, #C9A84C 50%, #8A7028 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', letterSpacing: '-0.028em', lineHeight: 1 }}>
        {number}
      </div>
      <h3 style={{ marginTop: 16, fontSize: `clamp(1.33rem, ${DS2.type.cardTitle.pref}vw, ${DS2.type.cardTitle.max}px)`, fontFamily: DS2.font.heading, fontWeight: 700, letterSpacing: '0.029em', color: DT.color.textPrimary }}>
        {title}
      </h3>
      {/* Figma card body line-height is 209% (46px/22px) — deliberately airy, not the usual 1.6 */}
      <p style={{ marginTop: 8, fontSize: `clamp(0.875rem, ${DS2.type.cardBody.pref}vw, ${DS2.type.cardBody.max}px)`, lineHeight: 1.83, color: DS2.color.textBright, fontFamily: DS2.font.body }}>
        {body}
      </p>
    </Card>
  );
}
